/**
 * Custom Pagination Component for Backend Pagination
 */
import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function CustomPagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  actualItemsOnPage, // New prop for actual items displayed on current page
  onPageChange,
  onNextPage,
  onPrevPage,
  variant = "contained",
  color = "info",
}) {
  // Calculate entry range based on actual items displayed
  const startEntry = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endEntry =
    actualItemsOnPage !== undefined
      ? (currentPage - 1) * itemsPerPage + actualItemsOnPage
      : Math.min(currentPage * itemsPerPage, totalItems);

  if (totalPages <= 1) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" p={2}>
        <MDTypography variant="button" color="secondary" fontWeight="regular">
          Showing {totalItems} {totalItems === 1 ? "entry" : "entries"}
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems="center"
      p={2}
      gap={1.5}
    >
      <MDBox mb={{ xs: 1, sm: 0 }}>
        <MDTypography variant="button" color="secondary" fontWeight="regular">
          Showing {startEntry} to {endEntry} of {totalItems} entries
        </MDTypography>
      </MDBox>

      <MDBox display="flex" alignItems="center" gap={0.5}>
        <MDButton
          variant="outlined"
          color={color}
          size="small"
          onClick={onPrevPage}
          disabled={currentPage <= 1}
        >
          Previous
        </MDButton>

        <MDBox display="flex" alignItems="center" gap={0.5} mx={1}>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <MDButton
                key={pageNum}
                variant={currentPage === pageNum ? "contained" : "outlined"}
                color={color}
                size="small"
                onClick={() => onPageChange(pageNum)}
                sx={{ minWidth: "40px" }}
              >
                {pageNum}
              </MDButton>
            );
          })}
        </MDBox>

        <MDButton
          variant="outlined"
          color={color}
          size="small"
          onClick={onNextPage}
          disabled={currentPage >= totalPages}
        >
          Next
        </MDButton>
      </MDBox>
    </MDBox>
  );
}

CustomPagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  actualItemsOnPage: PropTypes.number, // Optional prop for actual items on current page
  onPageChange: PropTypes.func.isRequired,
  onNextPage: PropTypes.func.isRequired,
  onPrevPage: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["contained", "gradient"]),
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ]),
};

export default CustomPagination;
